// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { Box, Button, ButtonGroup, Theme, useTheme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { websiteHomePath } from "@outschool/routes";
import { Container } from "@outschool/ui-legacy-component-library";
import { pxToRem, useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import HeaderTags from "../components/HeaderTags";
import SimpleLayout from "../components/layouts/SimpleLayout";
import RedirectWrapper from "../components/parentDashboardV2/components/RedirectWrapper";
import { ParentAgenda } from "../components/parentSchedule/ParentAgenda";
import { ParentScheduleMyClassesList } from "../components/parentSchedule/ParentScheduleMyClassesList";

export enum ViewType {
  Classes = "classes",
  Agenda = "agenda",
  Week = "week",
  Month = "month",
}
type ViewItem = {
  label: string;
  value: ViewType;
};

export enum TabType {
  Enrolled = "enrolled",
  Waitlisted = "waitlisted",
}

function useChangeView() {
  const navigate = useNavigation();
  return React.useCallback(
    (newView: ViewType, newTab?: TabType) => {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set("view", newView);
      newTab ? queryParams.set("tab", newTab) : queryParams.delete("tab");

      navigate(`${window.location.pathname}?${queryParams.toString()}`, {
        replace: true,
      });
    },
    [navigate],
  );
}

interface ViewButtonGroupProps {
  currentView: ViewType;
}

const ViewButtonGroup = ({ currentView }: ViewButtonGroupProps) => {
  const { t } = useTranslation("client\\routes\\ParentSchedule");
  const isMobile = useIsMobile();
  const changeView = useChangeView();

  const availableViews: ViewItem[] = [
    {
      label: t("All Classes"),
      value: ViewType.Classes,
    },
    {
      label: t("Agenda"),
      value: ViewType.Agenda,
    },
    ...(!isMobile
      ? [
          {
            label: t("Week"),
            value: ViewType.Week,
          },
          {
            label: t("Month"),
            value: ViewType.Month,
          },
        ]
      : []),
  ];

  React.useEffect(() => {
    if (
      !currentView ||
      // disallow "week" and "month" mobile views
      (isMobile &&
        (currentView === ViewType.Week || currentView === ViewType.Month))
    ) {
      // Default view is "classes > enrolled"
      changeView(ViewType.Classes);
    }
  }, [currentView, changeView, isMobile]);

  return (
    <ButtonGroup variant="outlined" fullWidth={isMobile}>
      {availableViews.map(({ label, value }) => (
        <Button
          key={value}
          variant={value === currentView ? "contained" : "outlined"}
          onClick={() => changeView(value)}
        >
          {label}
        </Button>
      ))}
    </ButtonGroup>
  );
};

interface TabGroupProps {
  currentTab: TabType;
}

const TabGroup = ({ currentTab }: TabGroupProps) => {
  const changeView = useChangeView();

  React.useEffect(() => {
    if (!currentTab) {
      changeView(ViewType.Classes, TabType.Enrolled);
    }
  }, [currentTab, changeView]);

  return (
    <Container>
      {Object.entries(TabType).map(([label, tab]) => (
        <Button
          variant="text"
          sx={{
            paddingX: pxToRem(20),
            marginX: pxToRem(5),
            borderRadius: 0,
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            boxShadow: "none",
            backgroundColor: "transparent",
            "&:disabled": {
              backgroundColor: "white",
              boxShadow: `0 0 2rem 0 rgba(0,0,0,0.1)`,
              // Clip box-shadow on the bottom
              clipPath: `inset(-100vh -100vw 0 -100vw)`,
            },
          }}
          key={tab}
          disabled={currentTab === tab}
          onClick={() => changeView(ViewType.Classes, tab)}
        >
          {label}
        </Button>
      ))}
    </Container>
  );
};

const ParentSchedulePage = () => {
  const { query } = useLocation();
  const {
    view: currentView,
    tab: currentTab,
  }: { view: ViewType; tab: TabType } = query;

  const { t } = useTranslation("client\\routes\\ParentSchedule");
  const theme = useTheme();

  return (
    <SimpleLayout>
      <HeaderTags title={t`Learner Schedule`} />
      <Box
        sx={(theme: Theme) => ({
          paddingY: 16,
          paddingX: 18,
          [theme.breakpoints.up("md")]: {
            paddingY: 30,
          },
          textAlign: "center",
          backgroundColor: "grey.50",
        })}
      >
        <ViewButtonGroup currentView={currentView} />
      </Box>
      {currentView === "classes" && (
        <Box sx={{ backgroundColor: "grey.50" }}>
          <TabGroup currentTab={currentTab} />
        </Box>
      )}
      <Container
        sx={{
          marginY: "2.5rem",
          [theme.breakpoints.up("md")]: {
            marginY: "5rem",
          },
        }}
      >
        {
          {
            classes: <ParentScheduleMyClassesList currentTab={currentTab} />,
            agenda: <ParentAgenda />,
            week: <ParentAgenda />,
            month: <ParentAgenda />,
          }[currentView]
        }
      </Container>
    </SimpleLayout>
  );
};

const ParentSchedulePageWrapper = () => {
  const { query } = useLocation();
  const { view, tab }: { view: ViewType; tab: TabType } = query;

  const redirectUrl =
    view === ViewType.Classes && tab
      ? `${websiteHomePath()}?tab=${tab}`
      : websiteHomePath();

  return (
    <RedirectWrapper to={redirectUrl}>
      <ParentSchedulePage />;
    </RedirectWrapper>
  );
};

export default ParentSchedulePageWrapper;
